import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./style.scss";
import customStyles from "../../../customStyles";
import Select from "react-select";
import editIcon from "../../../assets/icons/edit-icon-white.svg";
import {
  getProfileDetailsRequest,
  updateProfileDetails,
} from "../../../utils/apis/userApi";
import { useDispatch, useSelector } from "react-redux";
import { country } from "../../../utils/constants/country";
import Spinner from "../../../reusableComponents/spinner/Spinner";
import { returnMessages } from "../../../utils/store/reducers/Message";
import { returnErrors } from "../../../utils/store/reducers/Error";
import { setContact } from "../../../utils/store/reducers/authSlice";
import { DatePicker } from "antd/lib";
import dayjs from "dayjs";

const validCountryCodes = country.map((country) => country.code);

const nameRegex = /^[A-Za-z\s'-]+$/;

// Validation schema
const validationSchema = Yup.object({
  first_name: Yup.string()
    .required("First Name is required")
    .min(2, "First Name must be at least 2 characters long")
    .matches(
      nameRegex,
      "First Name can only contain letters, spaces, hyphens, or apostrophes"
    ),

  last_name: Yup.string()
    .required("Last Name is required")
    .min(2, "Last Name must be at least 2 characters long")
    .matches(
      nameRegex,
      "Last Name can only contain letters, spaces, hyphens, or apostrophes"
    ),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  country: Yup.string().required("Country is required"),
  country_code: Yup.string()
    .required("Country code is required")
    .oneOf(validCountryCodes, "Invalid country code"),
  contact: Yup.string()
    .typeError("Phone number must be a number")
    .required("Phone number is required")
    .max(15, "Contact Number must be less than 15 digits")
    .min(5, "Phone number must be at least 5 digits long"),
  city: Yup.string().required("City is required"),
  dob: Yup.string().required("Date of Birth is required"),
});

const PersonalInfo = ({ setProfileDetail }) => {
  const [profileDetails, setProfileDetails] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const { idToken } = useSelector((state) => state.auth);

  const fetchProfileDetails = async () => {
    try {
      const response = await getProfileDetailsRequest(idToken);
      setProfileDetails(response?.data);
      setProfileDetail(response?.data);
      dispatch(setContact(response?.data?.contact));
    } catch (error) {
      console.error("Error fetching profile details:", error);
    }
  };

  useEffect(() => {
    fetchProfileDetails();
  }, []);
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      first_name: profileDetails?.first_name || "",
      last_name: profileDetails?.last_name || "",
      email: profileDetails?.email || "",
      country: profileDetails?.country || "",
      country_code: profileDetails?.country_code || "",
      contact: profileDetails?.contact || "",
      city: profileDetails?.city || "",
      dob: profileDetails?.dob || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        ...values,
      };
      try {
        const response = await updateProfileDetails(payload);
        setEditMode(false);
        if (response.status < 399) {
          dispatch(setContact(payload.contact));
          dispatch(returnMessages("Profile updated successfully", 200));
        } else {
          dispatch(returnErrors("Error updating profile", 400));
        }
      } catch (error) {
        console.error("Error updating profile:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (profileDetails) {
      formik.setValues({
        first_name: profileDetails.first_name || "",
        last_name: profileDetails.last_name || "",
        email: profileDetails.email || "",
        country: profileDetails.country || "",
        country_code: profileDetails.country_code || "",
        contact: profileDetails.contact || "",
        city: profileDetails.city || "",
        dob: profileDetails.dob || "",
      });
    }
  }, [profileDetails]);
  const customComponents = {
    IndicatorSeparator: () => null,
  };
  console.log(
    country
      .map((item) => ({
        value: item.code,
        label: item.country,
      }))
      .find((item) => item.label === formik.values.country),
    "kjhasjdhajda"
  );
  if (!editMode) {
    customComponents.DropdownIndicator = () => null;
  }

  return (
    <div className="personal_info_container">
      <div className="personal_info_wrapper">
        <div className="header_wrapper">
          <h2>Personal Information</h2>
          {editMode ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button onClick={formik.handleSubmit} className="submit_button">
                {formik.isSubmitting ? (
                  <Spinner size={30} stroke={3} />
                ) : (
                  "Submit"
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditMode(!editMode);
                  formik.resetForm();
                }}
                className="edit_button"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditMode(!editMode)}
              className="edit_button"
            >
              Edit <img src={editIcon} alt="" />
            </button>
          )}
        </div>
        <form className="personal_info_form">
          <div className="personal_info_form_input">
            <label htmlFor="first_name" className="form_input_label">
              First name
              <input
                type="text"
                className="form_input_box"
                id="first_name"
                name="first_name"
                placeholder="First Name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.first_name}
                disabled={!editMode}
              />
              {formik.touched.first_name && formik.errors.first_name ? (
                <div className="error_message">{formik.errors.first_name}</div>
              ) : null}
            </label>
          </div>
          <div className="personal_info_form_input">
            <label htmlFor="last_name" className="form_input_label">
              Last name
              <input
                type="text"
                className="form_input_box"
                id="last_name"
                name="last_name"
                placeholder="Last Name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.last_name}
                disabled={!editMode}
              />
              {formik.touched.last_name && formik.errors.last_name ? (
                <div className="error_message">{formik.errors.last_name}</div>
              ) : null}
            </label>
          </div>
          <div className="personal_info_form_input">
            <label htmlFor="dob" className="form_input_label">
              Date of Birth
              <DatePicker
                onChange={(date) => {
                  formik.setFieldValue(
                    "dob",
                    date ? dayjs(date).format("YYYY-MM-DD") : ""
                  );
                }}
                className="form_input_box"
                onBlur={formik.handleBlur}
                value={formik.values.dob ? dayjs(formik.values.dob) : null}
                inputReadOnly
                disabledDate={(current) =>
                  current && current.isAfter(dayjs(), "day")
                }
                placeholder="Date of Birth"
                disabled={!editMode}
                defaultPickerValue={dayjs(dayjs().subtract(17, "years"))}
              />
              {formik.touched.dob && formik.errors.dob ? (
                <div className="error_message">{formik.errors.dob}</div>
              ) : null}
            </label>
          </div>
          <div className="personal_info_form_input">
            <label htmlFor="email" className="form_input_label">
              Email
              <input
                type="text"
                className="form_input_box"
                id="email"
                name="email"
                placeholder="Email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                disabled={!editMode || formik.values.email}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="error_message">{formik.errors.email}</div>
              ) : null}
            </label>
          </div>
          <div className="personal_info_form_input">
            <label htmlFor="country" className="form_input_label">
              Country
              <Select
                className="form_input_box react-select-container"
                classNamePrefix="react-select"
                name="country"
                placeholder="Select country"
                styles={customStyles}
                id="country"
                isSearchable={true}
                options={country.map((item) => ({
                  value: item.code,
                  label: item.country,
                }))}  
                onChange={(option) => {
                  formik.setFieldValue("country", option.label);
                  formik.setFieldValue("country_code", option.value);
                }}
                onBlur={formik.handleBlur}
                value={country
                  .map((item) => ({
                    value: item.code,
                    label: item.country,
                  }))
                  .find((item) => item.label === formik.values.country)}
                isDisabled={!editMode}
                components={customComponents}
              />
              {formik.touched.country && formik.errors.country ? (
                <div className="error_message">{formik.errors.country}</div>
              ) : null}
            </label>
          </div>
          <div className="personal_info_form_input personal_info_contact_input">
            <label className="form_input_label" htmlFor="user-countryCode">
              Contact Number
            </label>
            <div className="contact_input_box">
              <div className="for_contact">
                <label
                  htmlFor="user_countryCode"
                  className="contact_form_input country_code"
                >
                  <span
                    className="form_country_code_input"
                    style={{
                      color: "#fff",
                      position: "relative",
                      top: "0.5px",
                    }}
                  >
                    +
                  </span>
                  <input
                    type="text"
                    name="country_code"
                    placeholder="Country Code"
                    id="user_countryCode"
                    className="form_input_box"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.country_code}
                    disabled={!editMode}
                  />
                </label>
                {formik.touched.country_code && formik.errors.country_code ? (
                  <div className="error_message">
                    {formik.errors.country_code}
                  </div>
                ) : null}
              </div>
              <div className="for_contact">
                <label
                  htmlFor="user-phoneNumber"
                  className="contact_form_input phone_no"
                >
                  <input
                    type="number"
                    name="contact"
                    placeholder="Phone Number"
                    id="user-phoneNumber"
                    className="form_input_box"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.contact}
                    disabled={!editMode}
                  />
                </label>{" "}
                {formik.touched.contact && formik.errors.contact ? (
                  <div className="error_message">{formik.errors.contact}</div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="personal_info_form_input">
            <label htmlFor="city" className="form_input_label">
              City
              <input
                type="text"
                className="form_input_box"
                id="city"
                name="city"
                placeholder="City"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.city}
                disabled={!editMode}
              />
              {formik.touched.city && formik.errors.city ? (
                <div className="error_message">{formik.errors.city}</div>
              ) : null}
            </label>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalInfo;
